/**
 * Shoppable Subscription Page Custom Element
 * 
 * Usage:
 * 1. Load this script in your theme (add to theme.liquid or specific template)
 * 2. Use the custom element anywhere in your HTML:
 *    <shoppable-subscription-page></shoppable-subscription-page>
 * 
 * Optional attributes:
 * - page-handle: The handle of the page to load (default: 'shoppable-subscription-landing-page')
 *   Example: <shoppable-subscription-page page-handle="my-custom-page"></shoppable-subscription-page>
 * 
 * The element will fetch and render the content from the page template with layout: none
 * 
 * Events:
 * - 'shoppable-subscription-loaded': Fired when content is successfully loaded
 * 
 * Methods:
 * - reload(): Programmatically reload the content
 */
class ShoppableSubscriptionPage extends HTMLElement {
  constructor() {
    super();
    this.isLoaded = false;
    this.pageHandle = this.getAttribute('page-handle') || 'shoppable-subscription-landing-page';
  }

  connectedCallback() {
    // Only render when explicitly triggered (not automatically on connect)
    // This will be called after element is injected in the DOM
    if (!this.isLoaded && this.hasAttribute('auto-load')) {
      this.render();
    }
  }

  // Method to manually trigger loading
  load() {
    if (!this.isLoaded) {
      this.render();
    }
  }

  async render() {
    try {
      // Show loading state
      this.innerHTML = `
        <div class="shoppable-subscription-loading" style="text-align: center; padding: 2rem;">
          <div class="spinner" style="margin: 0 auto;"></div>
          <p>Loading...</p>
        </div>
      `;

      // Fetch the page content
      const url = `/pages/${this.pageHandle}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load content: ${response.status}`);
      }

      const html = await response.text();

      // Parse the HTML and extract the featured-products element
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const featuredProductsElement = doc.querySelector('featured-products');

      if (featuredProductsElement) {
        // Inject only the featured-products element
        this.innerHTML = featuredProductsElement.outerHTML;
      }
      
      this.isLoaded = true;

      // Dispatch custom event for any post-render logic
      this.dispatchEvent(new CustomEvent('shoppable-subscription-loaded', {
        bubbles: true,
        detail: { element: this }
      }));

      lazyImageObserver.observe();
      // Reinitialize Yotpo widgets
      if (typeof yotpoWidgetsContainer !== 'undefined' && yotpoWidgetsContainer.initWidgets) {
        yotpoWidgetsContainer.initWidgets();
      }
      // Re-initialize any Shopify or theme scripts that might be needed
      this.reinitializeScripts();

    } catch (error) {
      console.error('Error loading shoppable subscription page:', error);
      this.innerHTML = `
        <div class="shoppable-subscription-error" style="text-align: center; padding: 2rem; color: #d32f2f;">
          <p>Failed to load content. Please try again later.</p>
        </div>
      `;
    }
  }

  reinitializeScripts() {
    // Find and execute any inline scripts in the loaded content
    const scripts = this.querySelectorAll('script');
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    // Trigger any custom theme events (e.g., for sliders, carousels)
    if (typeof window.theme !== 'undefined' && window.theme.init) {
      window.theme.init();
    }

    // Dispatch a DOM content loaded event for the new content
    const event = new Event('DOMContentLoaded', {
      bubbles: true,
      cancelable: true
    });
    this.dispatchEvent(event);
  }

  // Method to reload content programmatically
  reload() {
    this.isLoaded = false;
    this.render();
  }
}

// Register the custom element
if (!customElements.get('shoppable-subscription-page')) {
  customElements.define('shoppable-subscription-page', ShoppableSubscriptionPage);
}

// Auto-inject custom element on subscription proxy page when no subscriptions exist
let checkInterval = null;

function checkAndInjectShoppableSubscription() {
  // Check if we're on the subscription proxy page
  if (window.location.pathname.includes('/apps/retextion/shop')) {
    // Look for the "no subscriptions" message
    const noSubscriptionElement = document.querySelector('.MuiTypography-body1');
    
    if (noSubscriptionElement && 
        noSubscriptionElement.textContent.trim() === 'You do not have any subscriptions yet.') {
      
      // Check if we haven't already injected the element
      const existingElement = document.querySelector('shoppable-subscription-page');
      if (!existingElement) {
        // Add container class to #retextion-root
        const retextionRoot = document.querySelector('#retextion-root');
        if (retextionRoot) {
          retextionRoot.classList.add('container');
        }
        
        // Create and inject the custom element
        const shoppableElement = document.createElement('shoppable-subscription-page');
        shoppableElement.setAttribute('auto-load', 'true');
        
        // Insert after the "no subscriptions" message
        noSubscriptionElement.insertAdjacentElement('afterend', shoppableElement);
        
        // Remove the "no subscriptions" message from DOM
        noSubscriptionElement.classList.add('d-none');
        
        // Manually trigger the load after injection
        shoppableElement.load();
        
        // Clear the interval once element is injected
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
      }

      const navigationElement = document.querySelector('[data-stay-selector="subDetails_subscriptionsNavigation"]');
      if (navigationElement) {
        navigationElement.innerHTML = `
          <div class="shoppable-subscription-navigation h3 text-decoration-underline mx-auto">
            <a href="/account">My Account</a>
          </div>
        `;
      }
    }
  }
}

// Run on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkAndInjectShoppableSubscription);
} else {
  checkAndInjectShoppableSubscription();
}

// Run interval check every 1 second and store the interval ID
checkInterval = setInterval(checkAndInjectShoppableSubscription, 1000);

// Clear interval after 10 seconds if element is not found
setTimeout(() => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
}, 20000);

